import { useState } from "react";
import PageLayout from "@/components/aoria/PageLayout";
import PageHeader from "@/components/aoria/PageHeader";
import Seo from "@/components/aoria/Seo";

/**
 * Encode pour Netlify Forms (même convention que sur la home).
 */
function encode(data) {
    return Object.keys(data)
        .map(
            (key) =>
                encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
        )
        .join("&");
}

export default function Contact() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", message: "" });

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({
                    "form-name": "contact",
                    "bot-field": "",
                    ...form,
                }),
            });
            if (!res.ok) throw new Error("submit failed");
            setStatus("success");
            setForm({ name: "", email: "", message: "" });
        } catch {
            setStatus("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageLayout testid="contact-page">
            <Seo
                title="Contact · Écrire à Aoria : presse, partenariats, recherche"
                description="Écrire à Aoria : presse, partenariats institutionnels, recherche, retours d'expérience. Nous répondons à chaque message sous quelques jours ouvrés."
                path="/contact"
            />
            <PageHeader
                testid="contact"
                eyebrow="CONTACT"
                title="Écrire à Aoria,"
                italic="presse, partenariats, recherche"
                lead="Presse, partenariats institutionnels, recherche, propositions éditoriales, retours d'expérience : nous lisons chaque message et répondons sous quelques jours ouvrés."
                backdrop="lines"
            />

            <section className="max-w-3xl mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
                <form
                    data-testid="contact-page-form"
                    name="contact"
                    method="POST"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    onSubmit={submit}
                    className="card-aoria !p-8 md:!p-10"
                >
                    <input type="hidden" name="form-name" value="contact" />
                    <p className="hidden">
                        <label>
                            Ne pas remplir : <input name="bot-field" />
                        </label>
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label
                                htmlFor="contact-page-name"
                                className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]"
                            >
                                Nom
                            </label>
                            <input
                                id="contact-page-name"
                                data-testid="contact-page-name"
                                type="text"
                                name="name"
                                required
                                value={form.name}
                                onChange={update("name")}
                                className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="contact-page-email"
                                className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]"
                            >
                                E-mail
                            </label>
                            <input
                                id="contact-page-email"
                                data-testid="contact-page-email"
                                type="email"
                                name="email"
                                required
                                value={form.email}
                                onChange={update("email")}
                                className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none"
                            />
                        </div>
                    </div>

                    <div className="mt-6">
                        <label
                            htmlFor="contact-page-message"
                            className="block text-xs uppercase tracking-[0.18em] text-[#5C6B7D]"
                        >
                            Votre message
                        </label>
                        <textarea
                            id="contact-page-message"
                            data-testid="contact-page-message"
                            name="message"
                            rows={6}
                            required
                            value={form.message}
                            onChange={update("message")}
                            className="mt-2 w-full bg-transparent border-b border-[#0D1B2A]/40 focus:border-[#0D1B2A] py-3 outline-none resize-none"
                        />
                    </div>

                    <button
                        type="submit"
                        data-testid="contact-page-submit"
                        disabled={loading}
                        className="btn-primary mt-10 rounded-full px-7 py-3.5 text-sm tracking-[0.15em] uppercase disabled:opacity-60"
                    >
                        {loading ? "Envoi…" : "Envoyer le message"}
                    </button>

                    {status && (
                        <div
                            data-testid={`contact-page-feedback-${status}`}
                            role="status"
                            aria-live="polite"
                            className={`mt-6 text-sm ${
                                status === "success"
                                    ? "text-[#0D1B2A]"
                                    : "text-[#9B2C2C]"
                            }`}
                        >
                            {status === "success"
                                ? "Message bien reçu. Merci de votre prise de contact, nous revenons vers vous rapidement."
                                : "Une erreur est survenue. Merci de réessayer dans un instant."}
                        </div>
                    )}
                </form>

                <p className="mt-12 text-sm text-[#5C6B7D] italic">
                    Aoria, Association loi 1901, SIREN 993 013 549. Les
                    coordonnées postales figurent sur la page{" "}
                    <a
                        href="/mentions-legales"
                        className="link-underline text-[#0D1B2A]"
                    >
                        Mentions légales
                    </a>
                    .
                </p>
            </section>
        </PageLayout>
    );
}
